import { useCallback, useEffect, useImperativeHandle, useRef, useState, type RefObject } from 'react'
import { Button } from '@biznomi/ui-react'

/**
 * 손글씨 서명판.
 *
 * 폰에서 손가락으로 그리는 게 기본 사용처라 다음 세 가지가 실제 함정이었다.
 *
 *  ① 그리는 동안 화면이 스크롤된다 → `touch-action: none` + preventDefault.
 *     CSS 만으로는 iOS Safari 에서 새는 경우가 있어 리스너를 passive:false 로 직접 붙인다.
 *  ② 레티나에서 선이 뭉갠다 → 캔버스 실제 픽셀을 devicePixelRatio 배로 잡고 컨텍스트를 스케일.
 *  ③ 화면 회전·리사이즈 때 캔버스를 다시 잡으면 그리던 서명이 지워진다 →
 *     리사이즈 시 기존 그림을 떠서 다시 그린다.
 */

export interface SignaturePadHandle {
  /** 그린 게 있으면 PNG data URL, 없으면 null */
  toDataURL: () => string | null
  clear: () => void
}

interface Props {
  ref?: RefObject<SignaturePadHandle | null>
  /** 비었는지 바뀔 때마다 알려준다 — 부모의 «서명 완료» 버튼 활성화용 */
  onEmptyChange?: (empty: boolean) => void
  disabled?: boolean
}

export function SignaturePad({ ref, onEmptyChange, disabled }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const dirty = useRef(false)
  const last = useRef<{ x: number; y: number } | null>(null)
  const [empty, setEmpty] = useState(true)

  const markDirty = useCallback(() => {
    if (dirty.current) return
    dirty.current = true
    setEmpty(false)
    onEmptyChange?.(false)
  }, [onEmptyChange])

  const setupContext = useCallback((ctx: CanvasRenderingContext2D, ratio: number) => {
    ctx.scale(ratio, ratio)
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#0f172a'
  }, [])

  // 캔버스 크기 잡기. 리사이즈 때 그리던 서명을 잃지 않게 떠서 다시 그린다
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      if (rect.width === 0) return
      const ratio = window.devicePixelRatio || 1

      // 기존 그림 백업 (크기가 이미 잡혀 있을 때만)
      let snapshot: HTMLCanvasElement | null = null
      if (canvas.width > 0 && dirty.current) {
        snapshot = document.createElement('canvas')
        snapshot.width = canvas.width
        snapshot.height = canvas.height
        snapshot.getContext('2d')?.drawImage(canvas, 0, 0)
      }

      canvas.width = Math.round(rect.width * ratio)
      canvas.height = Math.round(rect.height * ratio)

      const ctx = canvas.getContext('2d')
      if (!ctx) return
      setupContext(ctx, ratio)
      if (snapshot) {
        ctx.drawImage(snapshot, 0, 0, rect.width, rect.height)
      }
    }

    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    return () => observer.disconnect()
  }, [setupContext])

  // 포인터 이벤트. touchmove 를 passive:false 로 직접 막아 스크롤을 끊는다
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || disabled) return

    const point = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }

    const down = (e: PointerEvent) => {
      e.preventDefault()
      canvas.setPointerCapture(e.pointerId)
      drawing.current = true
      last.current = point(e)
    }

    const move = (e: PointerEvent) => {
      if (!drawing.current) return
      e.preventDefault()
      const ctx = canvas.getContext('2d')
      const p = point(e)
      if (!ctx || !last.current) return
      ctx.beginPath()
      ctx.moveTo(last.current.x, last.current.y)
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
      last.current = p
      markDirty()
    }

    const up = (e: PointerEvent) => {
      if (!drawing.current) return
      drawing.current = false
      // 점 하나만 찍은 경우도 서명으로 친다
      const ctx = canvas.getContext('2d')
      if (ctx && last.current) {
        ctx.beginPath()
        ctx.arc(last.current.x, last.current.y, 1.2, 0, Math.PI * 2)
        ctx.fill()
        markDirty()
      }
      last.current = null
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId)
    }

    const blockScroll = (e: TouchEvent) => {
      if (drawing.current) e.preventDefault()
    }

    canvas.addEventListener('pointerdown', down)
    canvas.addEventListener('pointermove', move)
    canvas.addEventListener('pointerup', up)
    canvas.addEventListener('pointercancel', up)
    canvas.addEventListener('touchmove', blockScroll, { passive: false })
    return () => {
      canvas.removeEventListener('pointerdown', down)
      canvas.removeEventListener('pointermove', move)
      canvas.removeEventListener('pointerup', up)
      canvas.removeEventListener('pointercancel', up)
      canvas.removeEventListener('touchmove', blockScroll)
    }
  }, [disabled, markDirty])

  const clear = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.restore()
    dirty.current = false
    setEmpty(true)
    onEmptyChange?.(true)
  }, [onEmptyChange])

  useImperativeHandle(
    ref,
    () => ({
      toDataURL: () => {
        if (!dirty.current) return null
        return canvasRef.current?.toDataURL('image/png') ?? null
      },
      clear,
    }),
    [clear],
  )

  return (
    <div className="space-y-2">
      <div className="relative rounded-card border-2 border-dashed border-slate-300 bg-white">
        <canvas
          ref={canvasRef}
          aria-label="서명란"
          className="block h-40 w-full touch-none rounded-card"
          style={{ touchAction: 'none' }}
        />
        {empty && (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-slate-300">
            여기에 서명해 주세요
          </p>
        )}
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="secondary" size="sm" onClick={clear} disabled={empty || disabled}>
          지우기
        </Button>
      </div>
    </div>
  )
}
